import * as THREE from "three";
import type { BoardSpec } from "../contexts/board";
import { FollowCameraRig } from "./camera/follow-camera-rig";
import type { MontageLook } from "./cinematic/montage-look";
import type { CameraPose } from "./cinematic/shots";
import { DebugOverlay } from "./debug/debug-overlay";
import { Hud } from "./hud/hud";
import { createPose, interpolateTransformInto } from "./math/pose-interpolation";
import type { PresentationConfig } from "./presentation.config";
import type { RenderFrame, SceneSetup } from "./render-frame";
import type { Renderer } from "./renderer";
import type { BoardMesh } from "./scene/board-mesh";
import { buildBoardMesh } from "./scene/board-mesh";
import { FeetMesh } from "./scene/feet-mesh";
import type { LevelAssets, LevelMesh } from "./scene/level-mesh";
import { buildLevelMesh, createLevelAssets, disposeLevelAssets } from "./scene/level-mesh";
import { buildSky } from "./scene/sky";
import { TextureLibrary } from "./textures/texture-library";
import { Toast } from "./ui/toast";

/** What the scene holds (leak checks: a map switch must leave these where they were). */
export interface SceneStats {
  /** Objects in the scene graph. */
  readonly objects: number;
  /** GPU geometries and textures alive (`WebGLRenderer.info.memory`). */
  readonly geometries: number;
  readonly textures: number;
}

/** Longest frame delta fed to smoothing (tab switches, breakpoints), s. */
const MAX_FRAME_DT_S = 0.1;
/** A jump of the board larger than this between frames is a reset: snap the camera, m. */
const TELEPORT_DISTANCE_M = 3;
const Y_AXIS = new THREE.Vector3(0, 1, 0);

export interface ThreeRendererOptions {
  readonly canvas: HTMLCanvasElement;
  /** Injected config; in dev builds a tunable clone, so camera/HUD values apply live. */
  readonly config: PresentationConfig;
  /** Parent of the HUD overlay. Defaults to the canvas's parent (or `document.body`). */
  readonly hudParent?: HTMLElement;
}

/**
 * Three.js implementation of the presentation `Renderer` (STYLE.md). Reads `RenderFrame`s
 * only: it never mutates domain state and never touches physics (REQUIREMENTS §2.3 rule 4).
 */
export class ThreeRenderer implements Renderer {
  private readonly config: PresentationConfig;
  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly sun: THREE.DirectionalLight;
  private readonly sky: THREE.Mesh;
  private readonly hemi: THREE.HemisphereLight;
  private readonly lookScratch = new THREE.Color();
  private readonly rig: FollowCameraRig;
  private readonly hud: Hud;
  private readonly debug: DebugOverlay;
  private readonly toast: Toast;
  private readonly feet: FeetMesh;
  private readonly boardPivot = new THREE.Group();
  private board: BoardMesh | null = null;
  private level: LevelMesh | null = null;
  private spec: BoardSpec | null = null;
  private readonly textures: TextureLibrary;
  private readonly levelAssets: LevelAssets;

  // Per-frame scratch (no allocation in `render`).
  private readonly pose = createPose();
  private readonly sunOffset = new THREE.Vector3();
  private readonly headingQuat = new THREE.Quaternion();
  private readonly lastBoardPosition = new THREE.Vector3();
  private lastTimeMs: number | null = null;
  private wheelAngleRad = 0;
  private wheelSpinRadps = 0;
  private flashAgeS = Number.POSITIVE_INFINITY;
  private hasRendered = false;
  // Montage hooks (src/game/montage): a cinematic camera pose and a fixed frame time.
  private cameraOverride: CameraPose | null = null;
  private frameDtOverrideS: number | null = null;
  private readonly onResize = (): void => this.resize();

  constructor(options: ThreeRendererOptions) {
    const { canvas, config } = options;
    this.config = config;
    this.canvas = canvas;
    const { palette, lighting, camera: cam } = config;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping =
      config.renderer.toneMapping === "aces"
        ? THREE.ACESFilmicToneMapping
        : THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = config.renderer.toneMappingExposure;
    this.renderer.shadowMap.enabled = true;
    // PCFSoftShadowMap was folded into PCFShadowMap (three r18x); softness comes from `shadow.radius`.
    this.renderer.shadowMap.type = THREE.PCFShadowMap;

    this.scene.fog = new THREE.Fog(palette.skyHorizon, lighting.fogNearM, lighting.fogFarM);
    // Shared level materials: CC0 textures load once, reused by every level (and the grip).
    const anisotropy = Math.min(
      config.surfaces.anisotropy,
      this.renderer.capabilities.getMaxAnisotropy(),
    );
    this.textures = new TextureLibrary(anisotropy);
    this.levelAssets = createLevelAssets(config, this.textures, anisotropy);
    this.camera = new THREE.PerspectiveCamera(cam.fovDeg, 1, cam.nearM, cam.farM);

    this.sky = buildSky(config);
    this.scene.add(this.sky);

    this.hemi = new THREE.HemisphereLight(
      lighting.hemiSkyColor,
      lighting.hemiGroundColor,
      lighting.hemiIntensity,
    );
    this.scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight(lighting.sunColor, lighting.sunIntensity);
    this.sun.castShadow = true;
    const shadow = this.sun.shadow;
    const e = lighting.shadowHalfExtentM;
    shadow.mapSize.set(lighting.shadowMapSizePx, lighting.shadowMapSizePx);
    Object.assign(shadow.camera, {
      left: -e,
      right: e,
      top: e,
      bottom: -e,
      near: 0.5,
      far: lighting.sunDistanceM * 2,
    });
    shadow.camera.updateProjectionMatrix();
    shadow.radius = lighting.shadowRadiusPx;
    shadow.bias = lighting.shadowBias;
    shadow.normalBias = lighting.shadowNormalBias;
    this.scene.add(this.sun, this.sun.target);

    this.boardPivot.name = "board-pivot";
    this.scene.add(this.boardPivot);
    this.feet = new FeetMesh(config);
    this.scene.add(this.feet.group);

    this.rig = new FollowCameraRig(config.camera);
    const hudParent = options.hudParent ?? canvas.parentElement ?? document.body;
    this.hud = new Hud(hudParent, config);
    this.debug = new DebugOverlay(hudParent, config);
    this.toast = new Toast(hudParent);
    this.scene.add(this.debug.group);
    this.boardPivot.add(this.debug.axes);

    window.addEventListener("resize", this.onResize);
    this.resize();
  }

  setup(setup: SceneSetup): void {
    this.board?.group.removeFromParent();
    this.board?.dispose();
    this.level?.group.removeFromParent();
    this.level?.dispose();

    this.spec = setup.boardSpec;
    this.board = buildBoardMesh(setup.boardSpec, this.config, this.textures);
    this.boardPivot.add(this.board.group);
    this.level = buildLevelMesh(setup.level, this.config, this.levelAssets);
    this.scene.add(this.level.group);
    this.rig.snap();
  }

  render(frame: RenderFrame): void {
    const nowMs = performance.now();
    const dtS =
      this.frameDtOverrideS ??
      (this.lastTimeMs === null ? 0 : Math.min(MAX_FRAME_DT_S, (nowMs - this.lastTimeMs) / 1000));
    this.lastTimeMs = nowMs;

    this.handleEvents(frame);
    this.updateBoard(frame, dtS);

    const board = frame.currentBoard;
    const riderHeadingRad = lerpAngleRad(
      frame.previousRider.headingRad,
      frame.rider.headingRad,
      frame.alpha,
    );
    this.rig.update(
      this.pose,
      board.linearVelocityMps,
      board.grounded,
      board.airtimeS,
      frame.stance,
      dtS,
      riderHeadingRad,
    );
    const eye = this.cameraOverride?.eye ?? this.rig.eye;
    const look = this.cameraOverride?.target ?? this.rig.target;
    const fovDeg = this.cameraOverride?.fovDeg ?? this.rig.fovDeg;
    this.camera.position.set(eye[0], eye[1], eye[2]);
    this.camera.lookAt(look[0], look[1], look[2]);
    if (Math.abs(this.camera.fov - fovDeg) > 1e-3) {
      this.camera.fov = fovDeg;
      this.camera.updateProjectionMatrix();
    }
    this.sky.position.copy(this.camera.position);

    this.updateSun();
    if (this.spec !== null) {
      // Feet are upright in the RIDER frame (MECHANICS.md): the rider heading, not the board.
      this.headingQuat.setFromAxisAngle(Y_AXIS, riderHeadingRad);
      this.feet.update(
        frame.previousRider,
        frame.rider,
        frame.alpha,
        this.headingQuat,
        frame.stance,
        {
          stickX: { front: frame.intents.front.stick.x, back: frame.intents.back.stick.x },
          airborne: !board.grounded,
          dtS,
          boardPose: this.boardPivot,
        },
      );
    }
    this.debug.update(frame, dtS);
    this.hud.update(frame, dtS);
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Montage hook: draw the next frames from this camera pose instead of the follow rig
   * (null = back to the follow rig), and advance time-based effects by exactly `dtS` per
   * frame instead of the wall clock (null = wall clock), for deterministic recording.
   */
  setMontageOverrides(cameraPose: CameraPose | null, frameDtS: number | null): void {
    this.cameraOverride = cameraPose;
    this.frameDtOverrideS = frameDtS;
  }

  /**
   * Montage hook: a shot's own look (the promo's desk match: a dark, warm "desk" ground and
   * warmer light), blended in by `weight` (0 = the game's own look, 1 = the full look).
   * Recomputed from the config every call, so it is safe to call every frame and to end
   * with `null`.
   */
  setMontageLook(look: MontageLook | null, weight = 1): void {
    const w = look === null ? 0 : Math.min(1, Math.max(0, weight));
    const l = this.config.lighting;
    const mix = (base: string, to: string | undefined): THREE.Color =>
      this.lookScratch.set(base).lerp(new THREE.Color(to ?? base), w);
    this.sun.color.copy(mix(l.sunColor, look?.sunColor));
    this.sun.intensity = l.sunIntensity * (1 + ((look?.sunIntensityScale ?? 1) - 1) * w);
    this.hemi.color.copy(mix(l.hemiSkyColor, look?.hemiSkyColor));
    this.hemi.groundColor.copy(mix(l.hemiGroundColor, look?.hemiGroundColor));
    this.hemi.intensity = l.hemiIntensity * (1 + ((look?.hemiIntensityScale ?? 1) - 1) * w);
    // The ground slab's material: its palette colour (scaled like the texture set does once
    // it has loaded), towards the look's ground colour.
    const ground = this.levelAssets.surfaces.get("ground");
    const spec = this.config.surfaces.ground;
    const base = new THREE.Color(spec.color);
    if (ground.map !== null) base.multiplyScalar(1 / this.config.surfaces.detailMeanLinear);
    const target = new THREE.Color(look?.groundColor ?? spec.color);
    if (ground.map !== null) target.multiplyScalar(1 / this.config.surfaces.detailMeanLinear);
    ground.color.copy(base.lerp(target, w));
  }

  /** The WebGL canvas (the montage recorder composites it with the video HUD). */
  get domElement(): HTMLCanvasElement {
    return this.canvas;
  }

  /** A short, subtle message (the checkpoint toast). */
  showToast(text: string): void {
    this.toast.show(text);
  }

  /** Scene graph and GPU memory counts (dev handle: leak checks after a map switch). */
  sceneStats(): SceneStats {
    let objects = 0;
    this.scene.traverse(() => {
      objects += 1;
    });
    const { geometries, textures } = this.renderer.info.memory;
    return { objects, geometries, textures };
  }

  /** Debug overlay toggle (also bound to F1). */
  setDebugEnabled(enabled: boolean): void {
    this.debug.setEnabled(enabled);
  }

  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    this.board?.dispose();
    this.level?.dispose();
    disposeLevelAssets(this.levelAssets);
    this.textures.dispose();
    this.feet.dispose();
    this.debug.dispose();
    this.hud.dispose();
    this.toast.dispose();
    this.sky.geometry.dispose();
    (this.sky.material as THREE.Material).dispose();
    this.renderer.dispose();
  }

  private handleEvents(frame: RenderFrame): void {
    for (const event of frame.recentEvents) {
      switch (event.type) {
        case "BoardLanded":
          this.rig.notifyLanding(event.velocityMps.y);
          break;
        case "RiderBailed":
          this.rig.notifyBail();
          break;
        case "TrickLanded":
          this.flashAgeS = 0;
          break;
        default:
          break;
      }
    }
  }

  private updateBoard(frame: RenderFrame, dtS: number): void {
    const pose = this.pose;
    interpolateTransformInto(
      pose,
      frame.previousBoard.transform,
      frame.currentBoard.transform,
      frame.alpha,
    );
    this.boardPivot.position.set(pose.px, pose.py, pose.pz);
    this.boardPivot.quaternion.set(pose.qx, pose.qy, pose.qz, pose.qw);
    this.boardPivot.updateMatrixWorld(true);

    if (
      this.hasRendered &&
      this.lastBoardPosition.distanceTo(this.boardPivot.position) > TELEPORT_DISTANCE_M
    ) {
      this.rig.snap();
    }
    this.lastBoardPosition.copy(this.boardPivot.position);
    this.hasRendered = true;

    const board = this.board;
    if (board === null || this.spec === null) return;
    // The deck leans on its trucks (a model value, the body does not tilt).
    board.deckPivot.rotation.x = frame.leanRad;

    // Wheels spin from the forward speed while rolling; they coast down in the air.
    const snap = frame.currentBoard;
    const v = snap.linearVelocityMps;
    const q = snap.transform.rotation;
    // Board +X in world: first column of the rotation matrix.
    const noseX = 1 - 2 * (q.y * q.y + q.z * q.z);
    const noseY = 2 * (q.x * q.y + q.w * q.z);
    const noseZ = 2 * (q.x * q.z - q.w * q.y);
    const forwardMps = v.x * noseX + v.y * noseY + v.z * noseZ;
    if (snap.grounded) {
      this.wheelSpinRadps = forwardMps / this.spec.wheels.radiusM;
    } else {
      this.wheelSpinRadps *= this.config.board.airborneSpinKeepPerS ** dtS;
    }
    this.wheelAngleRad = (this.wheelAngleRad - this.wheelSpinRadps * dtS) % (Math.PI * 2);
    for (const wheel of board.wheels) wheel.rotation.z = this.wheelAngleRad;

    // Clean catch: subtle white flash on the deck (STYLE.md: 80 ms).
    this.flashAgeS += dtS;
    const { catchFlashS, catchFlashIntensity } = this.config.board;
    board.deckMaterial.emissiveIntensity =
      this.flashAgeS < catchFlashS ? catchFlashIntensity * (1 - this.flashAgeS / catchFlashS) : 0;
  }

  /** The sun (and its tight shadow frustum) follows the board so shadows stay crisp. */
  private updateSun(): void {
    const l = this.config.lighting;
    const cosEl = Math.cos(l.sunElevationRad);
    this.sunOffset.set(
      Math.cos(l.sunAzimuthRad) * cosEl,
      Math.sin(l.sunElevationRad),
      -Math.sin(l.sunAzimuthRad) * cosEl,
    );
    this.sun.target.position.copy(this.boardPivot.position);
    this.sun.position
      .copy(this.boardPivot.position)
      .addScaledVector(this.sunOffset, l.sunDistanceM);
  }

  private resize(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio || 1, this.config.renderer.maxPixelRatio),
    );
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }
}

/** Shortest-way interpolation between two angles, rad. */
function lerpAngleRad(a: number, b: number, t: number): number {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * t;
}
